import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { mainURL } from "../utils/common";

// Configure these to match your filestore service
const TENANT_ID = "km";
const FILESTORE_URL_API = "/filestore/v1/files/url";
const FILESTORE_BINARY_API = "/filestore/v1/files/id";

// Add more IDs in reading order: [page1, page2, page3, page4, ...]
const FILE_STORE_IDS = [
  "6ba6c3d0-0f8f-48b2-8838-c06202703a18",
  "4e08b6ce-8876-464c-9792-c014431e258d",
  "c617d04e-4853-4026-8113-10a55ce398f0",
  "0b0725e8-2e08-46db-b1d7-2e5506c70bd8",
];

async function resolveFileStoreUrl(fileStoreId) {
  const params = new URLSearchParams({
    tenantId: TENANT_ID,
    fileStoreIds: fileStoreId,
  });
  const response = await fetch(`${mainURL}/filestore/v1/files/id?fileStoreId=${fileStoreId}&tenantId=km.kolkata`);

  if (!response.ok) {
    throw new Error(`URL lookup failed for ${fileStoreId}`);
  }

  const data = await response.json();
  const url =
    data?.[fileStoreId] ||
    data?.fileStoreIds?.[fileStoreId] ||
    data?.files?.[fileStoreId];

  if (!url) {
    throw new Error(`No download URL returned for ${fileStoreId}`);
  }

  return url;
}

async function downloadFileStoreBlob(fileStoreId) {
  // Prefer URL lookup, then download the binary
  try {
    const fileUrl = await resolveFileStoreUrl(fileStoreId);
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`Download failed for ${fileStoreId}`);
    }
    return response.blob();
  } catch {
    const params = new URLSearchParams({
      tenantId: TENANT_ID,
      fileStoreId,
    });
    const response = await fetch(`${mainURL}/filestore/v1/files/id?fileStoreId=${fileStoreId}&tenantId=km.kolkata`);
    if (!response.ok) {
      throw new Error(`Download failed for ${fileStoreId}`);
    }
    return response.blob();
  }
}

function pairIntoSpreads(pageUrls) {
  const spreads = [];
  for (let i = 0; i < pageUrls.length; i += 2) {
    spreads.push({
      left: pageUrls[i],
      right: pageUrls[i + 1] || pageUrls[i],
    });
  }
  return spreads;
}

function useFileStorePages(fileStoreIds) {
  const [pageUrls, setPageUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const objectUrls = [];

    async function loadPages() {
      setLoading(true);
      setError(null);

      try {
        const blobs = await Promise.all(
          fileStoreIds.map((id) => downloadFileStoreBlob(id))
        );

        if (cancelled) return;

        const urls = blobs.map((blob) => {
          const url = URL.createObjectURL(blob);
          objectUrls.push(url);
          return url;
        });

        setPageUrls(urls);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load booklet images");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (fileStoreIds.length > 0) {
      loadPages();
    } else {
      setLoading(false);
      setPageUrls([]);
    }

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [fileStoreIds]);

  const spreads = useMemo(() => pairIntoSpreads(pageUrls), [pageUrls]);

  return { spreads, loading, error };
}

function EPurashree_slider() {
  const { t } = useTranslation();
  const { spreads, loading, error } = useFileStorePages(FILE_STORE_IDS);
  const [spreadIndex, setSpreadIndex] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState(null);

  const canGoNext = spreadIndex < spreads.length - 1;
  const canGoPrev = spreadIndex > 0;

  const handleFlip = useCallback(
    (direction) => {
      if (isFlipping || loading || spreads.length === 0) return;
      if (direction === "next" && !canGoNext) return;
      if (direction === "prev" && !canGoPrev) return;

      setFlipDirection(direction);
      setIsFlipping(true);
    },
    [isFlipping, canGoNext, canGoPrev, loading, spreads.length]
  );

  const onFlipEnd = useCallback(() => {
    if (flipDirection === "next") {
      setSpreadIndex((i) => i + 1);
    } else if (flipDirection === "prev") {
      setSpreadIndex((i) => i - 1);
    }
    setIsFlipping(false);
    setFlipDirection(null);
  }, [flipDirection]);

  if (loading) {
    return (
      <section className="section inner-pad1">
        <div className="container">
          <div className="booklet-status">
            {t("booklet.loading", "Loading booklet pages...")}
          </div>
        </div>
      </section>
    );
  }

  if (error || spreads.length === 0) {
    return (
      <section className="section inner-pad1">
        <div className="container">
          <div className="booklet-status booklet-status--error">
            {error || t("booklet.empty", "No booklet pages found.")}
          </div>
        </div>
      </section>
    );
  }

  const current = spreads[spreadIndex];
  const nextSpread = flipDirection === "next" ? spreads[spreadIndex + 1] : null;
  const prevSpread = flipDirection === "prev" ? spreads[spreadIndex - 1] : null;

  return (
    <section className="section inner-pad1">
      <div className="container">
        <div className="bannere-epurash">
          <div className="booklet-wrapper">
            <div
              className="booklet"
              role="group"
              aria-label={t("booklet.slider", "Booklet pages")}
            >
              {!(isFlipping && flipDirection === "prev") && (
                <div className="booklet-page booklet-page--left">
                  <img
                    src={current.left}
                    alt={`Page ${spreadIndex * 2 + 1}`}
                  />
                </div>
              )}

              {isFlipping && flipDirection === "prev" && (
                <div className="booklet-page booklet-page--left booklet-page--under">
                  <img src={prevSpread.left} alt="" />
                </div>
              )}

              <div className="booklet-page booklet-page--right">
                <img
                  src={
                    isFlipping && flipDirection === "next"
                      ? nextSpread.right
                      : isFlipping && flipDirection === "prev"
                        ? prevSpread.right
                        : current.right
                  }
                  alt={`Page ${spreadIndex * 2 + 2}`}
                />
              </div>

              {isFlipping && flipDirection === "next" && (
                <div
                  className="booklet-flip booklet-flip--forward"
                  onAnimationEnd={onFlipEnd}
                >
                  <div className="booklet-flip-face booklet-flip-face--front">
                    <img src={current.right} alt="" />
                  </div>
                  <div className="booklet-flip-face booklet-flip-face--back">
                    <img src={nextSpread.left} alt="" />
                  </div>
                </div>
              )}

              {isFlipping && flipDirection === "prev" && (
                <div
                  className="booklet-flip booklet-flip--backward"
                  onAnimationEnd={onFlipEnd}
                >
                  <div className="booklet-flip-face booklet-flip-face--front">
                    <img src={current.left} alt="" />
                  </div>
                  <div className="booklet-flip-face booklet-flip-face--back">
                    <img src={prevSpread.left} alt="" />
                  </div>
                </div>
              )}

              <button
                type="button"
                className="booklet-zone booklet-zone--prev"
                onClick={() => handleFlip("prev")}
                disabled={!canGoPrev || isFlipping}
                aria-label="Previous page"
              >
                <i className="bi bi-arrow-left-square-fill" />
              </button>
              <button
                type="button"
                className="booklet-zone booklet-zone--next"
                onClick={() => handleFlip("next")}
                disabled={!canGoNext || isFlipping}
                aria-label="Next page"
              >
                <i className="bi bi-arrow-right-square-fill" />
              </button>
            </div>

            <p className="booklet-hint">
              {spreadIndex + 1} / {spreads.length}
            </p>
            
          </div>
        </div>
      </div>

      
    </section>
  );
}

export default EPurashree_slider;
